import React, { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import { Form } from "antd";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridRow, StyledGridCol } from "@/common/styles";
import { getOptionsFromRefList } from "@/util/common_function";
import PersonalBasicInformation from "../PersonalInformationDetails/PersonalBasicInformation";
import AliasesList from "../PersonalInformationDetails/AliasesList";
import AddressesList from "../PersonalInformationDetails/AddressesList";
import { BookingsSectionTitle } from "../styles";
import { useDispatch, useSelector } from "react-redux";
import BookingsComments from "../BookingsComments";

const InmateAssociatesForm = forwardRef(
  ({ selectedAssociate, onFormChange }, associateFormRef) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [selectedAliases, setSelectedAliases] = useState(
      selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAliases || []
    );
    const [selectedAddresses, setSelectedAddresses] = useState(
      selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAddresses || []
    );
    const fullSearch = useSelector(state => state.fullSearch);

    useEffect(() => {
      if (selectedAssociate) {
        form.setFieldsValue({
          associateTypeID: selectedAssociate?.associateTypeID,
          relationship: selectedAssociate?.relationship,
          comments: selectedAssociate?.comments,
        });
        setSelectedAliases(
          selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAliases || []
        );
        setSelectedAddresses(
          selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAddresses || []
        );
      } else {
        form.resetFields();
        setSelectedAliases([]);
        setSelectedAddresses([]);
      }
    }, [selectedAssociate, form]);

    useEffect(() => {
      form.setFieldsValue({ bioAliases: selectedAliases });
    }, [selectedAliases, form]);

    useEffect(() => {
      form.setFieldsValue({ bioAddresses: selectedAddresses });
    }, [selectedAddresses, form]);

    useImperativeHandle(associateFormRef, () => ({ form }));

    return (
      <Form
        form={form}
        onValuesChange={(changedValues, allValues) => {
          if (onFormChange) onFormChange(allValues);
        }}
      >
        <StyledGridRow gutter={16}>
          <StyledGridCol span={8}>
            <FormItem
              label="Associate Type"
              name="associateTypeID"
              required={!fullSearch}
              rules={!fullSearch ? [{ required: true, message: "Associate Type is required" },] : []}
            >
              <Select
                options={getOptionsFromRefList(
                  "Inmate_Associate_Type",
                  false,
                  false,
                  true
                )}
                placeholder="Associate Type"
                showSearch={true}
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem
              label="Relationship"
              name="relationship"
              required={!fullSearch}
              rules={!fullSearch ? [{ required: true, message: "Relationship is required" },] : []}
            >
              <Select
                options={getOptionsFromRefList("Relationship")}
                placeholder="Relationship"
                showSearch={true}
                variant="outlined"
              />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>

        <BookingsSectionTitle>
          <span>Personal Information</span>
        </BookingsSectionTitle>
        <PersonalBasicInformation
          selectedAssociate={selectedAssociate?.jackets?.[0]?.bio?.[0]}
          form={form}
          associates={true}
        />

        <AliasesList 
          showQuickAddForm={false}
          selectedAliases={selectedAliases}
          associates={true}
          onAliasesChange={setSelectedAliases}
        />

        {/* Hidden field to pass aliases to form */}
        <FormItem name="bioAliases" hidden>
          <TextField />
        </FormItem>

        <AddressesList
          selectedAddresses={selectedAddresses}
          associates={true}
          onAddressesChange={setSelectedAddresses}
        />

        {/* Hidden field to pass addresses to form */}
        <FormItem name="bioAddresses" hidden>
          <TextField />
        </FormItem>

        <BookingsSectionTitle>
          <span>Comments</span>
        </BookingsSectionTitle>
        <FormItem name="comments">
          <TextField inputType="textarea" />
        </FormItem>
      </Form>
    );
  }
);

export default InmateAssociatesForm;
